import { memo, type FC } from "react";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";

const OPTIONS = [
  { value: "new_change_maker", label: "New Change-Maker (< 6 Months)" },
  {
    value: "rising_contributor",
    label: "Rising Contributor (6 Months – 1 Year)",
  },
  {
    value: "established_performer",
    label: "Established Performer (1–2 Years)",
  },
  { value: "stable_operator", label: "Stable Operator (3–5 Years)" },
  { value: "company_veteran", label: "Company Veteran (5+ Years)" },
];

type Slice = { buckets: string[] };
const defaultSlice: Slice = { buckets: [] };

const ContactTimeInRoleFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactTimeInRole");
  const { setSlice } = useCompanyFilterActions();

  return (
    <Box sx={{ p: 1 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={OPTIONS}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        value={OPTIONS.filter((o) => slice.buckets.includes(o.value))}
        onChange={(_, v) =>
          setSlice("contactTimeInRole", {
            buckets: (v as typeof OPTIONS).map((o) => o.value),
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={
              slice.buckets.length === 0 ? "Select time in role..." : ""
            }
            inputProps={{
              ...params.inputProps,
              "data-testid": "contacts-time-in-role-input",
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((opt, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={opt.label} size="small" {...tagProps} />
            );
          })
        }
        size="small"
      />
    </Box>
  );
});

ContactTimeInRoleFilter.displayName = "ContactTimeInRoleFilter";

export const contactTimeInRoleModule: FilterModule<Slice> = {
  key: "contactTimeInRole",
  defaultSlice,
  Component: ContactTimeInRoleFilter,
  toCompanyFilters: (slice): Partial<CompanyFilters> =>
    slice.buckets.length > 0
      ? { contactFilters: { timeInRoleBuckets: slice.buckets } }
      : {},
  fromCompanyFilters: (filters): Slice => ({
    buckets: filters.contactFilters?.timeInRoleBuckets ?? [],
  }),
  getChips: (slice, setSlice) =>
    slice.buckets.map((b) => {
      const opt = OPTIONS.find((o) => o.value === b);
      return {
        id: `contacts-time-in-role-${b}`,
        label: opt ? opt.label : b,
        filterKey: "contactTimeInRole" as const,
        onRemove: () =>
          setSlice({ buckets: slice.buckets.filter((x) => x !== b) }),
      };
    }),
  getCount: (slice) => slice.buckets.length,
};
